import { createSlice } from "@reduxjs/toolkit";
import { resolveApiMessage } from "../../helpers/apiMessage";

import { createTable, deleteTable, getTables, updateTable } from "./thunk";

export interface TablesState {
    tables: any[];
    loading: boolean;
    submitting: boolean;
    error: string | null;
}

export const initialState: TablesState = {
    tables: [],
    loading: false,
    submitting: false,
    error: null,
};

const extractPayloadData = (payload: any): any => {
    if (!payload) {
        return undefined;
    }

    if (typeof payload === "object" && "data" in payload) {
        return (payload as Record<string, any>).data;
    }

    return payload;
};

const extractResponsePayload = (payload: any): any => {
    if (payload && typeof payload === "object" && "response" in payload) {
        return extractPayloadData((payload as Record<string, any>).response);
    }

    return extractPayloadData(payload);
};

const parseTableList = (payload: any): any[] => {
    const data = extractResponsePayload(payload);

    if (Array.isArray(data)) {
        return data;
    }

    if (Array.isArray((data as any)?.items)) {
        return (data as any).items;
    }

    if (Array.isArray((data as any)?.tables)) {
        return (data as any).tables;
    }

    if (Array.isArray((data as any)?.data)) {
        return (data as any).data;
    }

    return [];
};

const parseTableItem = (payload: any): any | null => {
    const data = extractResponsePayload(payload);

    if (!data) {
        return null;
    }

    if (Array.isArray(data)) {
        return data[0] ?? null;
    }

    if (data && typeof data === "object") {
        if ((data as any).table && typeof (data as any).table === "object") {
            return (data as any).table;
        }

        if ((data as any).data && typeof (data as any).data === "object" && !Array.isArray((data as any).data)) {
            return (data as any).data;
        }
    }

    return data;
};

const getTableIdValue = (table: any): string | number | undefined => {
    if (!table || typeof table !== "object") {
        return undefined;
    }

    const candidateKeys = ["id", "tableId", "table_id", "uuid", "_id", "slug", "code"];

    for (const key of candidateKeys) {
        const value = (table as Record<string, any>)[key];
        if (value !== undefined && value !== null && value !== "") {
            return value as string | number;
        }
    }

    return undefined;
};

const toComparableId = (value: string | number | undefined | null): string | undefined => {
    if (value === undefined || value === null) {
        return undefined;
    }

    return String(value);
};

const upsertTable = (tables: any[], table: any, fallbackId?: string | number) => {
    if (!Array.isArray(tables)) {
        return;
    }

    const identifier = getTableIdValue(table) ?? fallbackId;
    const comparableIdentifier = toComparableId(identifier ?? null);

    if (!comparableIdentifier) {
        tables.push(table);
        return;
    }

    const index = tables.findIndex(
        (item) => toComparableId(getTableIdValue(item) ?? null) === comparableIdentifier
    );

    if (index >= 0) {
        tables[index] = {
            ...tables[index],
            ...table,
        };
        return;
    }

    tables.push(
        getTableIdValue(table) === undefined
            ? {
                  ...table,
                  id: identifier,
              }
            : table
    );
};

const removeTableById = (tables: any[], tableId: string | number): any[] => {
    const comparableIdentifier = toComparableId(tableId);

    if (!comparableIdentifier) {
        return tables;
    }

    return tables.filter((item) => toComparableId(getTableIdValue(item) ?? null) !== comparableIdentifier);
};

const getErrorMessage = (payload: any): string => {
    if (!payload) {
        return resolveApiMessage(payload);
    }

    if (typeof payload === "string") {
        return payload;
    }

    if (payload && typeof payload === "object") {
        if (typeof (payload as any).message === "string") {
            return (payload as any).message as string;
        }

        if (typeof (payload as any).error === "string") {
            return (payload as any).error as string;
        }
    }

    return resolveApiMessage(payload);
};

const tablesSlice = createSlice({
    name: "Tables",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getTables.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getTables.fulfilled, (state, action) => {
                state.loading = false;
                state.tables = parseTableList(action.payload);
            })
            .addCase(getTables.rejected, (state, action) => {
                state.loading = false;
                state.tables = [];
                const errorPayload = (action as any).payload ?? action.error;
                state.error = getErrorMessage(errorPayload);
            })
            .addCase(createTable.pending, (state) => {
                state.submitting = true;
                state.error = null;
            })
            .addCase(createTable.fulfilled, (state, action) => {
                state.submitting = false;
                const table = parseTableItem(action.payload);
                if (table) {
                    upsertTable(state.tables, table);
                }
            })
            .addCase(createTable.rejected, (state, action) => {
                state.submitting = false;
                const errorPayload = (action as any).payload ?? action.error;
                state.error = getErrorMessage(errorPayload);
            })
            .addCase(updateTable.pending, (state) => {
                state.submitting = true;
                state.error = null;
            })
            .addCase(updateTable.fulfilled, (state, action) => {
                state.submitting = false;
                const payload = action.payload as any;
                const table = parseTableItem(payload?.response ?? payload);
                const fallbackId = payload?.tableId;
                if (table) {
                    upsertTable(state.tables, table, fallbackId);
                } else if (fallbackId) {
                    upsertTable(state.tables, { id: fallbackId, ...payload?.request }, fallbackId);
                }
            })
            .addCase(updateTable.rejected, (state, action) => {
                state.submitting = false;
                const errorPayload = (action as any).payload ?? action.error;
                state.error = getErrorMessage(errorPayload);
            })
            .addCase(deleteTable.pending, (state) => {
                state.submitting = true;
                state.error = null;
            })
            .addCase(deleteTable.fulfilled, (state, action) => {
                state.submitting = false;
                const tableId = (action.payload as any)?.tableId;
                if (tableId !== undefined) {
                    state.tables = removeTableById(state.tables, tableId);
                }
            })
            .addCase(deleteTable.rejected, (state, action) => {
                state.submitting = false;
                const errorPayload = (action as any).payload ?? action.error;
                state.error = getErrorMessage(errorPayload);
            });
    },
});

export default tablesSlice.reducer;
